import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../api/api";
import toast from "react-hot-toast";
import { Edit, Plus, Trash2, FolderPlus } from "lucide-react";

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const res = await api.get("/projects?limit=100");
      setProjects(res.data.items || []);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const deleteProject = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this project?"
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      loadProjects();
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to delete project");
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-black text-[#0b6b1d]">
            Projects
          </h1>
          <p className="text-slate-500 mt-1">
            Manage infrastructure projects, categories and project updates.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/admin/projects/categories"
            className="px-5 py-3 rounded-2xl bg-white border border-slate-200 text-[#0b6b1d] font-black flex items-center gap-2"
          >
            <FolderPlus size={18} />
            Categories
          </Link>

          <Link
            to="/admin/projects/add"
            className="px-5 py-3 rounded-2xl bg-[#0b6b1d] hover:bg-[#ff6600] text-white font-black flex items-center gap-2"
          >
            <Plus size={18} />
            Add Project
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-6">Loading projects...</p>
        ) : projects.length === 0 ? (
          <p className="p-6 text-slate-500">No project has been created yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-black text-slate-600">
                    Project
                  </th>
                  <th className="text-left p-4 text-sm font-black text-slate-600">
                    Category
                  </th>
                  <th className="text-left p-4 text-sm font-black text-slate-600">
                    Location
                  </th>
                  <th className="text-left p-4 text-sm font-black text-slate-600">
                    Features
                  </th>
                  <th className="text-right p-4 text-sm font-black text-slate-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {projects.map((project) => (
                  <tr key={project.id} className="border-b last:border-b-0">
                    <td className="p-4">
                      <div className="flex items-center gap-4">
                        {project.featured_image ? (
                          <img
                            src={project.featured_image}
                            alt={project.title}
                            className="w-20 h-14 rounded-xl object-cover border"
                          />
                        ) : (
                          <div className="w-20 h-14 rounded-xl bg-slate-100 border" />
                        )}

                        <div>
                          <p className="font-black text-slate-900">
                            {project.title}
                          </p>
                          <p className="text-xs text-slate-500 line-clamp-1">
                            {project.summary}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="p-4">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-black text-white"
                        style={{
                          backgroundColor:
                            project.category?.color || "#0b6b1d",
                        }}
                      >
                        {project.category?.name || "Uncategorized"}
                      </span>
                    </td>

                    <td className="p-4 text-sm text-slate-600">
                      {project.location || "—"}
                    </td>

                    <td className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {project.features?.slice(0, 3).map((item) => (
                          <span
                            key={item}
                            className="px-3 py-1 rounded-xl bg-slate-100 text-[#0b6b1d] text-xs font-bold"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/admin/projects/edit/${project.id}`}
                          className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-[#0b6b1d] hover:text-white flex items-center justify-center"
                        >
                          <Edit size={17} />
                        </Link>

                        <button
                          onClick={() => deleteProject(project.id)}
                          className="w-10 h-10 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center"
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProjects;